/** Links that lead to an item wherever it is (a short link, a public link, a guest's): where they lead, and going. */
import { useEffect } from "preact/hooks";
import { api } from "../api/client.ts";
import type { LinkTarget } from "../api/types.gen.ts";
import { type Resource, useResource } from "./resource.ts";
import { navigate } from "./router.ts";
import { itemPath } from "./visibility.ts";

const never = () => false;

/**
 * Where the link at `path` (`/links/…`) leads, going there unless `stays` says to stay. Null: nothing to ask. `stay`:
 * this page stays (nothing to ask, the link couldn't say, or it said to stay). Nothing goes anywhere once the page
 * asking is gone.
 */
export function useLinkTarget(
  path: string | null,
  stays: (at: LinkTarget) => boolean = never,
): { r: Resource<LinkTarget | null>; stay: boolean } {
  const r = useResource(`link:${path ?? ""}`, (signal) =>
    path === null ? Promise.resolve(null) : api.get<LinkTarget>(path, undefined, signal),
  );
  const at = r.data;
  const go = at ? !stays(at) : false;
  useEffect(() => {
    if (at && go) {
      navigate(itemPath(at), true);
    }
  }, [at, go]);
  return { r, stay: path === null || r.error !== undefined || at === null || (at !== undefined && !go) };
}
